/**
 * AdminMailOrderModal.js
 * Create mail/check orders with customer price or 🏢 wholesale pricing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import LoadingSpinner from './LoadingSpinner';
import {
  getRetailPriceCents,
  resolveLineUnitPriceCents,
  getWholesaleMinQty,
  getWholesaleTierMeta,
  getEffectiveDiscountPct,
  productHasWholesaleTier,
  applyExtraDiscountCents,
  MAIL_ORDER_WHOLESALE_OPTIONS,
} from '../utils/wholesalePricing';
import { calculateMailOrderTotals } from '../utils/mailOrderTotals';
import {
  CA_PROVINCES,
  normalizeCanadianProvince,
  resolveCountryCode,
} from '../utils/addressUtils';
import { formatMailOrderShippingAddress, resolveMailOrderAddressFields } from '../utils/mailOrderAddress';
import './AdminMailOrderModal.css';
import MailOrderLineProductPicker from './MailOrderLineProductPicker';
import MailOrderCustomerSearch from './MailOrderCustomerSearch';
import {
  MAIL_ORDER_MODAL_TITLE,
  MAIL_ORDER_MODAL_SUBTITLE,
  MAIL_ORDER_CHECKOUT_PREFILL_HINT,
  MAIL_ORDER_PRICING_RETAIL_LABEL,
  MAIL_ORDER_PRICING_WHOLESALE_LABEL,
  MAIL_ORDER_WHOLESALE_TIER,
  MAIL_ORDER_MODE_LIVE,
  MAIL_ORDER_MODE_TEST,
  MAIL_ORDER_MODE_OPTIONS,
} from '../utils/mailOrderConstants';

const PAYMENT_METHODS = ['Check', 'Cash', 'Invoice', 'Money Order', 'Other'];

const emptyLine = () => ({
  key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  productId: '',
  quantity: 1,
  unitPriceCents: 0,
  manualDiscountPct: null,
});

const CHECK_PAYMENT_NOTE = `Please make your payment payable to Paul Cranford / Cranford Publications and include your order number on the memo line. We will ship your order once payment has cleared. Questions? Reply to this email or contact orders@cranfordpub.ca.`;

const AdminMailOrderModal = ({
  isOpen,
  onClose,
  onOrderCreated,
  emailInvoiceForOrder,
  initialDraft = null,
  title = MAIL_ORDER_MODAL_TITLE,
  subtitle = MAIL_ORDER_MODAL_SUBTITLE,
}) => {
  const { currentTheme } = useTheme();
  const appliedDraftRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shippingStreet, setShippingStreet] = useState('');
  const [shippingStreet2, setShippingStreet2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingPostalCode, setShippingPostalCode] = useState('');
  const [shippingCountry, setShippingCountry] = useState('CA');
  const [shippingProvince, setShippingProvince] = useState('');
  const [linkedUserId, setLinkedUserId] = useState(null);
  const [customerIsWholesaler, setCustomerIsWholesaler] = useState(false);

  const [pricingMode, setPricingMode] = useState('retail');
  const [lines, setLines] = useState([emptyLine()]);

  const [taxSettings, setTaxSettings] = useState([]);
  const [shippingCents, setShippingCents] = useState(0);
  const [taxCents, setTaxCents] = useState(0);
  const [shippingOverridden, setShippingOverridden] = useState(false);
  const [taxOverridden, setTaxOverridden] = useState(false);
  const [shippingCalcNote, setShippingCalcNote] = useState('');
  const [taxCalcNote, setTaxCalcNote] = useState('');
  const [calculatingTotals, setCalculatingTotals] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [notes, setNotes] = useState('');
  const [emailInvoice, setEmailInvoice] = useState(true);
  const [includeCheckInstructions, setIncludeCheckInstructions] = useState(true);
  const [mailOrderMode, setMailOrderMode] = useState(MAIL_ORDER_MODE_LIVE);

  const productMap = useMemo(() => {
    const map = new Map();
    products.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [products]);

  const computeWholesaleUnitPrice = useCallback((product, quantity) => {
    if (!product) return 0;
    return resolveLineUnitPriceCents(product, {
      pricingMode: 'wholesale',
      wholesaleTier: MAIL_ORDER_WHOLESALE_TIER,
      quantity,
      ...MAIL_ORDER_WHOLESALE_OPTIONS,
    });
  }, []);

  const resolveLinePrice = useCallback((line) => {
    const product = productMap.get(String(line.productId));
    if (!product) return 0;
    if (pricingMode !== 'wholesale') {
      return getRetailPriceCents(product);
    }
    if (productHasWholesaleTier(product, MAIL_ORDER_WHOLESALE_TIER)) {
      return computeWholesaleUnitPrice(product, line.quantity);
    }
    const retail = getRetailPriceCents(product);
    const pct = Number(line.manualDiscountPct);
    if (Number.isFinite(pct) && pct > 0) {
      return applyExtraDiscountCents(retail, pct);
    }
    return retail;
  }, [productMap, pricingMode, computeWholesaleUnitPrice]);

  const computedLines = useMemo(() => lines.map((line) => {
    const product = productMap.get(String(line.productId));
    const unitPrice = product ? resolveLinePrice(line) : 0;
    const retail = product ? getRetailPriceCents(product) : 0;
    const tierMeta = product
      ? getWholesaleTierMeta(product, MAIL_ORDER_WHOLESALE_TIER, line.quantity, MAIL_ORDER_WHOLESALE_OPTIONS)
      : null;
    return {
      ...line,
      product,
      unitPrice,
      lineTotal: unitPrice * (Number(line.quantity) || 0),
      retail,
      isWholesale: unitPrice < retail,
      effectiveDiscountPct: getEffectiveDiscountPct(retail, unitPrice),
      catalogDiscountPct: tierMeta?.catalogDiscountPct ?? 0,
      hasWholesaleTier: product ? productHasWholesaleTier(product, MAIL_ORDER_WHOLESALE_TIER) : false,
      needsManualDiscount: pricingMode === 'wholesale' && product && !productHasWholesaleTier(product, MAIL_ORDER_WHOLESALE_TIER),
      manualDiscountPct: line.manualDiscountPct,
      minWholesaleQty: product ? getWholesaleMinQty(product, MAIL_ORDER_WHOLESALE_TIER) : null,
    };
  }), [lines, productMap, resolveLinePrice, pricingMode]);

  const subtotalCents = useMemo(
    () => computedLines.reduce((sum, line) => sum + line.lineTotal, 0),
    [computedLines]
  );

  const totalCents = subtotalCents + (Number(shippingCents) || 0) + (Number(taxCents) || 0);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const [productsRes, taxRes] = await Promise.all([
        fetch('/api/admin/products', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch('/api/tax-settings'),
      ]);
      if (!productsRes.ok) throw new Error('Failed to load products');
      const data = await productsRes.json();
      const normalized = (Array.isArray(data) ? data : []).map((product) => {
        let wholesale_pricing = product.wholesale_pricing ?? product.wholesalePricing ?? null;
        if (typeof wholesale_pricing === 'string') {
          try {
            wholesale_pricing = JSON.parse(wholesale_pricing);
          } catch {
            wholesale_pricing = null;
          }
        }
        return { ...product, wholesale_pricing };
      });
      setProducts(normalized);
      if (taxRes.ok) {
        const taxData = await taxRes.json();
        setTaxSettings(Array.isArray(taxData) ? taxData : []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      appliedDraftRef.current = null;
      loadProducts();
    }
  }, [isOpen, loadProducts]);

  const applyInitialDraft = useCallback((draft) => {
    if (!draft) return;

    if (draft.customerName != null) setCustomerName(draft.customerName);
    if (draft.customerEmail != null) setCustomerEmail(draft.customerEmail);
    if (draft.customerPhone != null) setCustomerPhone(draft.customerPhone);
    const addressFields = resolveMailOrderAddressFields(draft);
    setShippingStreet(addressFields.street);
    setShippingStreet2(addressFields.street2);
    setShippingCity(addressFields.city);
    setShippingPostalCode(addressFields.postalCode);
    if (draft.shippingCountry != null) setShippingCountry(draft.shippingCountry);
    if (draft.shippingProvince != null) setShippingProvince(draft.shippingProvince);
    if (draft.linkedUserId != null) setLinkedUserId(draft.linkedUserId);
    if (draft.customerIsWholesaler != null) setCustomerIsWholesaler(draft.customerIsWholesaler);
    if (draft.pricingMode === 'wholesale' || draft.pricingMode === 'retail') {
      setPricingMode(draft.pricingMode);
    } else if (draft.customerIsWholesaler) {
      setPricingMode('wholesale');
    }
    if (Array.isArray(draft.lines) && draft.lines.length) {
      setLines(draft.lines.map((line) => ({
        ...emptyLine(),
        ...line,
        key: line.key || `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        productId: line.productId != null ? String(line.productId) : '',
      })));
    }
    if (draft.shippingCents != null) setShippingCents(Number(draft.shippingCents) || 0);
    if (draft.taxCents != null) setTaxCents(Number(draft.taxCents) || 0);
    if (draft.shippingOverridden != null) setShippingOverridden(Boolean(draft.shippingOverridden));
    if (draft.taxOverridden != null) setTaxOverridden(Boolean(draft.taxOverridden));
    if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
    if (draft.notes != null) setNotes(draft.notes);
    if (draft.emailInvoice != null) setEmailInvoice(Boolean(draft.emailInvoice));
    if (draft.includeCheckInstructions != null) {
      setIncludeCheckInstructions(Boolean(draft.includeCheckInstructions));
    }
    if (draft.mailOrderMode === MAIL_ORDER_MODE_LIVE || draft.mailOrderMode === MAIL_ORDER_MODE_TEST) {
      setMailOrderMode(draft.mailOrderMode);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !initialDraft || loadingProducts) return;
    const draftKey = JSON.stringify(initialDraft);
    if (appliedDraftRef.current === draftKey) return;
    appliedDraftRef.current = draftKey;
    applyInitialDraft(initialDraft);
  }, [isOpen, initialDraft, loadingProducts, applyInitialDraft]);

  const resetForm = () => {
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setShippingStreet('');
    setShippingStreet2('');
    setShippingCity('');
    setShippingPostalCode('');
    setShippingCountry('CA');
    setShippingProvince('');
    setLinkedUserId(null);
    setCustomerIsWholesaler(false);
    setPricingMode('retail');
    setLines([emptyLine()]);
    setShippingCents(0);
    setTaxCents(0);
    setShippingOverridden(false);
    setTaxOverridden(false);
    setShippingCalcNote('');
    setTaxCalcNote('');
    setPaymentMethod('Check');
    setNotes('');
    setEmailInvoice(true);
    setIncludeCheckInstructions(true);
    setMailOrderMode(MAIL_ORDER_MODE_LIVE);
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };

  const applyCustomerFromLookup = useCallback((c) => {
    if (!c) return;
    setCustomerName(c.name || '');
    setCustomerEmail(c.email || '');
    setCustomerPhone(c.phone || '');

    if (c.street) {
      setShippingStreet(c.street);
      setShippingStreet2(c.street2 || '');
      setShippingCity(c.city || '');
      setShippingPostalCode(c.postalCode || '');
    } else if (c.shippingAddress) {
      const parsed = resolveMailOrderAddressFields({ shippingAddress: c.shippingAddress });
      setShippingStreet(parsed.street);
      setShippingStreet2(parsed.street2);
      setShippingCity(parsed.city);
      setShippingPostalCode(parsed.postalCode);
    }

    if (c.country) {
      setShippingCountry(resolveCountryCode({ country: c.country }));
    }
    if (c.province) {
      setShippingProvince(
        resolveCountryCode({ country: c.country }) === 'CA'
          ? normalizeCanadianProvince(c.province)
          : c.province
      );
    }
    setShippingOverridden(false);
    setTaxOverridden(false);
    setLinkedUserId(c.userId || null);
    setCustomerIsWholesaler(Boolean(c.isWholesaler));
    if (c.isWholesaler) {
      setPricingMode('wholesale');
    }
    setError('');
  }, []);

  const handlePricingModeChange = (mode) => {
    setPricingMode(mode);
    setCustomerIsWholesaler(mode === 'wholesale');
  };

  const updateLine = (key, patch) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const handleManualDiscountChange = (key, value) => {
    const manualDiscountPct = value === '' ? null : Math.min(100, Math.max(0, parseFloat(value) || 0));
    updateLine(key, { manualDiscountPct });
  };

  const handleQuantityChange = (key, rawQty) => {
    const quantity = Math.max(1, parseInt(rawQty, 10) || 1);
    setLines((prev) => prev.map((line) => {
      if (line.key !== key) return line;
      return { ...line, quantity };
    }));
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (key) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((line) => line.key !== key)));
  };

  const handleProductChange = (key, productId) => {
    updateLine(key, { productId, manualDiscountPct: null, unitPriceCents: 0 });
  };

  useEffect(() => {
    setLines((prev) => prev.map((line) => {
      const product = productMap.get(String(line.productId));
      if (!product) return line;
      if (pricingMode === 'retail') {
        return { ...line, manualDiscountPct: null };
      }
      if (productHasWholesaleTier(product, MAIL_ORDER_WHOLESALE_TIER)) {
        return { ...line, manualDiscountPct: null };
      }
      return line;
    }));
  }, [pricingMode, productMap]);

  const recalculateTotals = useCallback(async ({
    forceShipping = false,
    forceTax = false,
  } = {}) => {
    const hasProducts = computedLines.some((line) => line.productId && line.product);
    if (!hasProducts) {
      if (forceShipping || !shippingOverridden) {
        setShippingCents(0);
        setShippingCalcNote('');
        if (forceShipping) setShippingOverridden(false);
      }
      if (forceTax || !taxOverridden) {
        setTaxCents(0);
        setTaxCalcNote('');
        if (forceTax) setTaxOverridden(false);
      }
      return;
    }

    setCalculatingTotals(true);
    try {
      const applyShipping = forceShipping || !shippingOverridden;
      const applyTax = forceTax || !taxOverridden;

      const baseParams = {
        computedLines,
        subtotalCents,
        countryCode: shippingCountry,
        province: shippingProvince,
        taxSettings,
      };

      if (applyShipping && applyTax) {
        const result = await calculateMailOrderTotals(baseParams);
        setShippingCents(result.shippingCents);
        setShippingCalcNote(result.shippingNote);
        setTaxCents(result.taxCents);
        setTaxCalcNote(result.taxNote);
        if (forceShipping) setShippingOverridden(false);
        if (forceTax) setTaxOverridden(false);
        return;
      }

      if (applyShipping) {
        const result = await calculateMailOrderTotals(baseParams);
        setShippingCents(result.shippingCents);
        setShippingCalcNote(result.shippingNote);
        if (forceShipping) setShippingOverridden(false);
        if (!taxOverridden) {
          const taxResult = await calculateMailOrderTotals({
            ...baseParams,
            shippingCentsOverride: result.shippingCents,
          });
          setTaxCents(taxResult.taxCents);
          setTaxCalcNote(taxResult.taxNote);
        }
        return;
      }

      if (applyTax) {
        const taxResult = await calculateMailOrderTotals({
          ...baseParams,
          shippingCentsOverride: shippingCents,
        });
        setTaxCents(taxResult.taxCents);
        setTaxCalcNote(taxResult.taxNote);
        if (forceTax) setTaxOverridden(false);
      }
    } catch (err) {
      console.error('Mail order totals calculation failed:', err);
    } finally {
      setCalculatingTotals(false);
    }
  }, [
    computedLines,
    subtotalCents,
    shippingCountry,
    shippingProvince,
    taxSettings,
    shippingOverridden,
    taxOverridden,
    shippingCents,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      recalculateTotals();
    }, 300);
    return () => clearTimeout(timer);
    // Recalc when cart/destination changes; respects manual shipping/tax overrides inside recalculateTotals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, computedLines, subtotalCents, shippingCountry, shippingProvince, taxSettings, shippingCents]);

  const formatMoneyInput = (cents) => (Number(cents || 0) / 100).toFixed(2);
  const parseMoneyInput = (value) => Math.max(0, Math.round(parseFloat(value || '0') * 100) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!customerName.trim() || !customerEmail.trim()) {
      setError('Customer name and email are required.');
      return;
    }

    const validLines = computedLines.filter((line) => line.productId && line.product);
    if (!validLines.length) {
      setError('Add at least one product line.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        customer: {
          name: customerName.trim(),
          email: customerEmail.trim(),
          phone: customerPhone.trim(),
          shippingAddress: formatMailOrderShippingAddress({
            street: shippingStreet,
            street2: shippingStreet2,
            city: shippingCity,
            province: shippingProvince,
            postalCode: shippingPostalCode,
            countryCode: shippingCountry,
          }),
        },
        items: validLines.map((line) => ({
          productId: Number(line.productId),
          quantity: Number(line.quantity) || 1,
          unitPriceCents: line.unitPrice,
        })),
        pricingMode,
        wholesaleTier: pricingMode === 'wholesale' ? MAIL_ORDER_WHOLESALE_TIER : null,
        subtotal: subtotalCents,
        shipping: Number(shippingCents) || 0,
        tax: Number(taxCents) || 0,
        total: totalCents,
        paymentMethod,
        paymentStatus: 'pending_payment',
        orderStatus: 'pending',
        notes: notes.trim(),
        userId: linkedUserId,
        mailOrderMode,
      };

      const response = await fetch('/api/orders/mail-order', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to create mail order');

      const order = result.order;

      if (emailInvoice && emailInvoiceForOrder) {
        await emailInvoiceForOrder(order, {
          checkInstructions: includeCheckInstructions ? CHECK_PAYMENT_NOTE : '',
        });
      }

      onOrderCreated?.(order);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create mail order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`mail-order-overlay theme-${currentTheme}`} onClick={handleClose}>
      <div className="mail-order-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="mail-order-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <button type="button" className="mail-order-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        {loadingProducts ? (
          <div className="mail-order-loading">
            <LoadingSpinner size="medium" message="Loading products…" />
          </div>
        ) : (
          <form className="mail-order-form" onSubmit={handleSubmit}>
            {error && <div className="mail-order-error">{error}</div>}
            {initialDraft && (
              <p className="mail-order-hint mail-order-draft-banner">{MAIL_ORDER_CHECKOUT_PREFILL_HINT}</p>
            )}

            <section className="mail-order-section mail-order-mode-section">
              <h4>Order type</h4>
              <div className="mail-order-pricing-row">
                {MAIL_ORDER_MODE_OPTIONS.map((option) => (
                  <label key={option.value} className="mail-order-radio">
                    <input
                      type="radio"
                      name="mailOrderMode"
                      value={option.value}
                      checked={mailOrderMode === option.value}
                      onChange={() => setMailOrderMode(option.value)}
                      disabled={submitting}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <p className="mail-order-hint">
                {MAIL_ORDER_MODE_OPTIONS.find((o) => o.value === mailOrderMode)?.hint}
              </p>
              {mailOrderMode === MAIL_ORDER_MODE_TEST && (
                <p className="mail-order-hint mail-order-hint--test">
                  Test orders use order numbers like <code>FID-MAIL-TEST-…</code> and invoice emails are prefixed <strong>[TEST]</strong>.
                </p>
              )}
            </section>

            <section className="mail-order-section">
              <h4>Customer</h4>
              <div className="mail-order-grid mail-order-grid--2">
                <div className="mail-order-span-2 mail-order-field">
                  <span className="mail-order-field-label">Find customer</span>
                  <MailOrderCustomerSearch
                    onSelect={applyCustomerFromLookup}
                    disabled={submitting}
                  />
                </div>
                <label>
                  Email
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    placeholder="customer@example.com"
                  />
                </label>
                <label>
                  Name
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </label>
                <label className="mail-order-span-2">
                  Street address
                  <input
                    type="text"
                    value={shippingStreet}
                    onChange={(e) => setShippingStreet(e.target.value)}
                    placeholder="123 Main Street"
                    autoComplete="shipping address-line1"
                  />
                </label>
                <label className="mail-order-span-2">
                  Apt / unit <span className="mail-order-optional">(optional)</span>
                  <input
                    type="text"
                    value={shippingStreet2}
                    onChange={(e) => setShippingStreet2(e.target.value)}
                    placeholder="Suite, apartment, etc."
                    autoComplete="shipping address-line2"
                  />
                </label>
                <label>
                  City
                  <input
                    type="text"
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder="Halifax"
                    autoComplete="shipping address-level2"
                  />
                </label>
                <label>
                  Postal / ZIP code
                  <input
                    type="text"
                    value={shippingPostalCode}
                    onChange={(e) => setShippingPostalCode(e.target.value)}
                    placeholder={shippingCountry === 'US' ? '90210' : 'B3H 1A1'}
                    autoComplete="shipping postal-code"
                  />
                </label>
                <label>
                  Ship to country
                  <select
                    value={shippingCountry}
                    onChange={(e) => {
                      setShippingCountry(e.target.value);
                      setShippingOverridden(false);
                      setTaxOverridden(false);
                    }}
                  >
                    <option value="CA">Canada</option>
                    <option value="US">United States</option>
                    <option value="INTL">International</option>
                  </select>
                </label>
                <label>
                  {shippingCountry === 'CA' ? 'Province' : shippingCountry === 'US' ? 'State' : 'Region'}
                  {shippingCountry === 'CA' ? (
                    <select
                      value={shippingProvince}
                      onChange={(e) => {
                        setShippingProvince(e.target.value);
                        setTaxOverridden(false);
                      }}
                    >
                      <option value="">Select province…</option>
                      {CA_PROVINCES.map((p) => (
                        <option key={p.code} value={p.code}>{p.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={shippingProvince}
                      onChange={(e) => {
                        setShippingProvince(e.target.value);
                        setTaxOverridden(false);
                      }}
                      placeholder={shippingCountry === 'US' ? 'e.g. NY' : 'Region (optional)'}
                    />
                  )}
                </label>
              </div>
              {shippingCountry === 'CA' && !shippingProvince && (
                <p className="mail-order-hint">Select a province for accurate HST/GST on books and shipping.</p>
              )}
              {customerIsWholesaler && pricingMode !== 'wholesale' && (
                <p className="mail-order-hint mail-order-hint--wholesale">
                  🏢 Registered wholesaler — consider 🏢 Wholesale pricing below.
                </p>
              )}
            </section>

            <section className="mail-order-section">
              <h4>Pricing</h4>
              <div className="mail-order-pricing-row">
                <label className="mail-order-radio">
                  <input
                    type="radio"
                    name="pricingMode"
                    value="retail"
                    checked={pricingMode === 'retail'}
                    onChange={() => handlePricingModeChange('retail')}
                  />
                  {MAIL_ORDER_PRICING_RETAIL_LABEL}
                </label>
                <label className="mail-order-radio">
                  <input
                    type="radio"
                    name="pricingMode"
                    value="wholesale"
                    checked={pricingMode === 'wholesale'}
                    onChange={() => handlePricingModeChange('wholesale')}
                  />
                  {MAIL_ORDER_PRICING_WHOLESALE_LABEL}
                </label>
              </div>
              {pricingMode === 'wholesale' && (
                <p className="mail-order-hint">
                  Line prices use each product&apos;s 🏢 Wholesale Pricing when configured.
                  For products without wholesale pricing, enter a manual discount % on the line.
                </p>
              )}
            </section>

            <section className="mail-order-section">
              <div className="mail-order-section-head">
                <h4>Line items</h4>
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>
                  + Add line
                </button>
              </div>
              <div className="mail-order-lines">
                {computedLines.map((line) => (
                  <div key={line.key} className="mail-order-line">
                    <MailOrderLineProductPicker
                      products={products}
                      value={line.productId}
                      onSelect={(productId) => handleProductChange(line.key, productId)}
                    />
                    <input
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(e) => handleQuantityChange(line.key, e.target.value)}
                      title="Quantity"
                      className="mail-order-qty"
                    />
                    <div className="mail-order-line-price">
                      <span className="mail-order-unit-price" title="Unit price">
                        ${formatMoneyInput(line.unitPrice)}
                        {pricingMode === 'wholesale' && line.retail > line.unitPrice && (
                          <small> (retail ${formatMoneyInput(line.retail)})</small>
                        )}
                      </span>
                      {pricingMode === 'wholesale' && line.product && (
                        <span className="mail-order-line-discount-meta">
                          {line.hasWholesaleTier && line.catalogDiscountPct > 0 ? (
                            <>{line.catalogDiscountPct}% wholesale discount</>
                          ) : line.needsManualDiscount ? (
                            <label className="mail-order-line-manual-discount">
                              <span>No 🏢 wholesale pricing — discount %</span>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={line.manualDiscountPct ?? ''}
                                placeholder="0"
                                onChange={(e) => handleManualDiscountChange(line.key, e.target.value)}
                                className="mail-order-line-manual-discount__input"
                                title="Manual discount off customer price"
                              />
                            </label>
                          ) : null}
                          {line.needsManualDiscount && line.manualDiscountPct > 0 && (
                            <span className="mail-order-line-manual-discount__applied">
                              {line.manualDiscountPct}% off → ${formatMoneyInput(line.unitPrice)}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <span className="mail-order-line-total">${formatMoneyInput(line.lineTotal)}</span>
                    <button
                      type="button"
                      className="mail-order-remove"
                      onClick={() => removeLine(line.key)}
                      title="Remove line"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="mail-order-section mail-order-totals">
              <div className="mail-order-section-head">
                <h4>Totals</h4>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => recalculateTotals({ forceShipping: true, forceTax: true })}
                  disabled={calculatingTotals}
                >
                  {calculatingTotals ? 'Calculating…' : '↻ Recalculate all'}
                </button>
              </div>
              <div className="mail-order-totals-grid">
                <label>
                  Subtotal
                  <input type="text" readOnly value={`$${formatMoneyInput(subtotalCents)}`} />
                </label>
                <label>
                  Shipping (CAD)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formatMoneyInput(shippingCents)}
                    onChange={(e) => {
                      setShippingCents(parseMoneyInput(e.target.value));
                      setShippingOverridden(true);
                    }}
                  />
                  <span className={`mail-order-calc-meta${shippingOverridden ? ' is-overridden' : ''}`}>
                    {shippingOverridden ? 'Manual override' : (shippingCalcNote || 'Auto-calculated')}
                    {shippingOverridden && (
                      <button
                        type="button"
                        className="mail-order-reset-calc"
                        onClick={() => recalculateTotals({ forceShipping: true })}
                      >
                        Use calculated
                      </button>
                    )}
                  </span>
                </label>
                <label>
                  Tax (CAD)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={formatMoneyInput(taxCents)}
                    onChange={(e) => {
                      setTaxCents(parseMoneyInput(e.target.value));
                      setTaxOverridden(true);
                    }}
                  />
                  <span className={`mail-order-calc-meta${taxOverridden ? ' is-overridden' : ''}`}>
                    {taxOverridden ? 'Manual override' : (taxCalcNote || 'Auto-calculated')}
                    {taxOverridden && (
                      <button
                        type="button"
                        className="mail-order-reset-calc"
                        onClick={() => recalculateTotals({ forceTax: true })}
                      >
                        Use calculated
                      </button>
                    )}
                  </span>
                </label>
                <label>
                  Total
                  <input type="text" readOnly className="mail-order-total-field" value={`$${formatMoneyInput(totalCents)}`} />
                </label>
              </div>
            </section>

            <section className="mail-order-section">
              <h4>Payment &amp; delivery</h4>
              <div className="mail-order-grid mail-order-grid--2">
                <label>
                  Payment method
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
                <label className="mail-order-span-2">
                  Internal notes
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional note for order history"
                  />
                </label>
              </div>
              <label className="mail-order-checkbox">
                <input
                  type="checkbox"
                  checked={emailInvoice}
                  onChange={(e) => setEmailInvoice(e.target.checked)}
                />
                Email invoice to customer when saved
              </label>
              {emailInvoice && (paymentMethod === 'Check' || paymentMethod === 'Money Order') && (
                <label className="mail-order-checkbox">
                  <input
                    type="checkbox"
                    checked={includeCheckInstructions}
                    onChange={(e) => setIncludeCheckInstructions(e.target.checked)}
                  />
                  Include check / mail payment instructions in email
                </label>
              )}
            </section>

            <div className="mail-order-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting
                  ? 'Saving…'
                  : mailOrderMode === MAIL_ORDER_MODE_TEST
                    ? (emailInvoice ? 'Create test order & email [TEST] invoice' : 'Create test order')
                    : (emailInvoice ? 'Create order & email invoice' : 'Create order')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminMailOrderModal;
